/**
 * Every user-facing sentence on the Import report's review items.
 *
 * A review item is a change an External EMR refresh could not settle on its
 * own: Roger's value beside External EMR's, for a person to choose. The
 * wording is settled product copy in the chart register, as in
 * importReportCopy.ts, and the tests assert it verbatim.
 */

import type { ImportReviewItemRead } from '../../services/api/types.gen';

export type ReviewChoice = NonNullable<ImportReviewItemRead['choices']>[number];

/** The section heading while items are open. */
export function reviewItemsHeading(openCount: number): string {
  return `${openCount} new ${openCount === 1 ? 'item' : 'items'} to review`;
}

/** The heading once every item is settled and the reader asked to see them. */
export const REVIEW_ITEMS_SETTLED_HEADING = 'Reviewed items';

/** Column labels on one item. */
export const ROGER_VALUE_LABEL = "Roger's value";
export const EXTERNAL_VALUE_LABEL = "External EMR's value";

/** The three answers a person can give. */
export const CHOICE_LABELS: Record<ReviewChoice, string> = {
  keep_roger: "Keep Roger's",
  take_external: "Take External EMR's",
  acknowledged: 'Acknowledge',
};

/** Settles every open item at once; Roger's values stay. */
export const ACKNOWLEDGE_ALL_LABEL = 'Acknowledge all';

/** How a settled item reads back. */
const SETTLED_VERBS: Record<string, string> = {
  keep_roger: "kept Roger's value",
  take_external: "took External EMR's value",
  acknowledged: 'acknowledged',
};

/** Closed by a later refresh, not by a person: the question no longer stood. */
export const SUPERSEDED_ACKNOWLEDGEMENT = 'superseded';

export function settledLine(
  name: string | null | undefined,
  formattedDate: string,
  acknowledgement: string | null | undefined
): string {
  if (acknowledgement === SUPERSEDED_ACKNOWLEDGEMENT) {
    return `Superseded by a later refresh on ${formattedDate}`;
  }
  const verb = SETTLED_VERBS[acknowledgement ?? ''] ?? 'acknowledged';
  return `${name ?? 'A colleague'} ${verb} on ${formattedDate}`;
}

/** A value with nothing in it. */
export const EMPTY_VALUE_COPY = 'none';

/**
 * One value as a short line.
 *
 * A diagnosis grid is a list of codes; a recert that moved is a small map of
 * the fields that changed. Both read as one line so the two columns sit
 * side by side.
 */
export function formatReviewValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY_VALUE_COPY;
  if (Array.isArray(value)) {
    return value.length === 0 ? EMPTY_VALUE_COPY : value.map(String).join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return EMPTY_VALUE_COPY;
    return entries
      .map(([key, entry]) => `${key}: ${entry ?? EMPTY_VALUE_COPY}`)
      .join(' · ');
  }
  return String(value);
}

/** Toasts. */
export const ACKNOWLEDGE_ERROR_COPY = 'Failed to record the acknowledgement';
export const REVIEW_ITEMS_ERROR_COPY =
  'Unable to load the items to review. Please try again.';

/** Shows the settled items under the open ones. */
export function showSettledLabel(count: number, expanded = false): string {
  return expanded ? `Hide ${count} reviewed` : `Show ${count} reviewed`;
}
